import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Box } from '@design-system/pc';
import { ChatbotGateway } from '@/di-container/chatbot/gateway/client';
import useAuthenticated from '@/hooks/useAuthenticated';

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    loadMessenger?: () => Promise<any>;
    __sendbird_messenger?: any;
    __initializeMessenger?: () => Promise<void>;
  }
}

// 커스텀 이벤트 타입 정의
interface MessengerErrorEvent extends Event {
  detail?: {
    message?: string;
  };
}
const APP_ID = process.env.NEXT_PUBLIC_SENDBIRD_APP_ID;
const AGENT_ID = process.env.NEXT_PUBLIC_SENDBIRD_AGENT_ID;
const LOADER_URL = process.env.NEXT_PUBLIC_SENDBIRD_LOADER_URL;
const CONTAINER_ID = 'sendbird-messenger-container';

const Sendbird = forwardRef((props, ref) => {
  const messengerRef = useRef<any>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isUpdateUserSession, setIsUpdateUserSession] = useState(false);
  const initializationRef = useRef(false);
  const { getChatbotToken } = ChatbotGateway();
  const { authInfo, isAuthenticated } = useAuthenticated();

  const toggleMessenger = useCallback(async () => {
    if (!messengerRef?.current || !isInitialized) return;
    if (typeof window === 'undefined') return;

    try {
      if (!isUpdateUserSession && authInfo?.custNo) {
        const res = await getChatbotToken.exec();

        await window.__sendbird_messenger.updateUserSession({
          userId: authInfo?.custNo ? String(authInfo?.custNo) : undefined,
          authToken: res?.data?.authToken,
        });
        setIsUpdateUserSession(true);
      }

      await window.__sendbird_messenger.open();
    } catch (e) {
      console.error('Error toggling messenger:', e);
    }
  }, [authInfo?.custNo, getChatbotToken, isInitialized, isUpdateUserSession]);

  const loadMessengerScript = useCallback(async () => {
    try {
      // 기존 스크립트 제거
      const existingScripts = document.querySelectorAll(
        'script[data-sendbird]',
      );
      existingScripts.forEach((script) => script.remove());

      // 스크립트 엘리먼트 생성
      const script = document.createElement('script');
      script.setAttribute('data-sendbird', 'true');
      script.type = 'module';

      // 스크립트 내용 직접 작성
      script.textContent = `
        const init = async () => {
          try {
            // 이미 초기화되었다면 스킵
            if (window.__sendbird_messenger) {
              return;
            }

            // 스크립트를 동적으로 로드
            const module = await import('${LOADER_URL}');

            // 컨테이너 요소 확인
            const container = document.getElementById('${CONTAINER_ID}');
            if (!container) {
              throw new Error('Container element not found');
            }

            
            // loadMessenger 함수 찾기
            const loadMessenger = module.loadMessenger || Object.values(module).find(
              (value) => typeof value === 'function' && value.name === 'u'
            );

            if (!loadMessenger) {
              throw new Error('loadMessenger function not found');
            }

            // 메신저 초기화
            const messenger = await loadMessenger({useShadowDOM: false});
            await messenger.initialize({
              appId: '${APP_ID}',
              aiAgentId: '${AGENT_ID}',
              context: {
                login: '${isAuthenticated.toString()}',
                timezone: 'Asia/Seoul'
              } 
            });
            
            // 메신저 인스턴스 저장
            window.__sendbird_messenger = messenger;
            window.dispatchEvent(new Event('messengerInitialized'));

          } catch (error) {
            console.error('Messenger initialization error:', error);
            window.dispatchEvent(new CustomEvent('messengerError', { detail: { message: error.message } }));
          }
        };

        // DOM이 완전히 로드된 후 초기화 실행
        if (document.readyState === 'complete') {
          init();
        } else {
          window.addEventListener('load', init, { once: true });
        }
      `;

      // 이벤트 리스너 설정
      const initHandler = () => {
        if (initializationRef.current) return;
        initializationRef.current = true;

        messengerRef.current = window.__sendbird_messenger;
        setIsInitialized(true);
      };

      const errorHandler = (event: Event) => {
        const errorEvent = event as MessengerErrorEvent;
        const errorMessage =
          errorEvent.detail?.message || 'Messenger initialization failed';
        console.error('Messenger error:', errorMessage);
      };

      // 이벤트 리스너 등록
      window.addEventListener('messengerInitialized', initHandler);
      window.addEventListener('messengerError', errorHandler);

      // 스크립트 추가
      document.head.appendChild(script);
      // document.getElementById('sb-agent-launcher').style.display = 'none';
      console.log('test', document.getElementById('sb-agent-launcher'));

      return () => {
        window.removeEventListener('messengerInitialized', initHandler);
        window.removeEventListener('messengerError', errorHandler);
      };
    } catch (error) {
      console.error('Script loading error:', error);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (initializationRef?.current) return;

    loadMessengerScript();

    return () => {
      try {
        const existingScripts = document.querySelectorAll(
          'script[data-sendbird]',
        );
        existingScripts.forEach((script) => script.remove());

        if (window.__sendbird_messenger) {
          window.__sendbird_messenger = undefined;
        }
        initializationRef.current = false;
        setIsInitialized(false);
      } catch (e) {
        console.error('Error destroying messenger:', e);
      }
    };
  }, [loadMessengerScript]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const observer = new MutationObserver(() => {
      const launcher = document.getElementById('sb-agent-launcher');
      if (launcher && launcher.style.display !== 'none') {
        launcher.style.display = 'none';
      }
    });

    // 초기 체크
    const launcher = document.getElementById('sb-agent-launcher');
    if (launcher) {
      launcher.style.display = 'none';
    }

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  useImperativeHandle(ref, () => ({
    toggleMessenger,
  }));

  return (
    <Box className="App">
      <div id={CONTAINER_ID} />
    </Box>
  );
});

Sendbird.displayName = 'Sendbird';

export default Sendbird;
